import React, {
  useState,
  useEffect,
  useMemo,
  useContext,
  ReactElement,
} from 'react';

import { useRouter } from 'next/router';
import clsx from 'clsx';
import { Element as ScrollElement } from 'react-scroll';
import { EventEmitter } from 'eventemitter3';

import { isMobile } from '@utils/window';

import { Container } from '@atoms';

import { AuthContext } from '@modules/base/auth';

import { RootContext } from '../../base/root/root-context';

import { NavLink } from './nav-link';
import { NavToggle } from './nav-toggle';
import { NavLogo } from './nav-logo';
import { NavLogout } from './nav-logout';
import { navLinks } from './nav.links';

import classes from './nav.module.scss';

export type NavProps = {
  backgroundTransparent?: boolean;
  minimalMode?: boolean;
  noShadow?: boolean;
  atlas?: boolean;
  component?: ReactElement;
  auth?: AuthContext;
  disableHideOnScroll?: boolean;
};

const MinimalNav = () => {
  return (
    <nav className={clsx(classes.nav, classes.minimal)}>
      <NavLogo minimal />
    </nav>
  );
};

const navEventsEmitter = new EventEmitter();

export const toggleNavVisible = (visible?: boolean) => {
  navEventsEmitter.emit('changeVisibility', visible);
};

export const Nav = ({
  backgroundTransparent,
  minimalMode,
  noShadow,
  atlas,
  component,
  auth,
  disableHideOnScroll,
}: NavProps) => {
  const router = useRouter();
  // MOBILE TOGGLE STATE
  const [isOpen, changeOpen] = useState(false);
  const [isScrolled, handleScroll] = useState(false);

  const [currentPath, setCurrentPath] = useState('');
  const { reminderVisible } = useContext(RootContext);
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    navEventsEmitter.on('changeVisibility', (visible?: boolean) => {
      setIsVisible((isVisible) => {
        if (typeof visible === 'boolean') return visible;
        return !isVisible;
      });
    });

    return () => {
      navEventsEmitter.removeListener('changeVisibility');
    };
  }, []);

  const links = useMemo(() => {
    return navLinks.map((navLink) => (
      <NavLink
        key={navLink.message.id}
        currentPath={currentPath}
        {...navLink}
        isScrolled={isScrolled}
      />
    ));
  }, [currentPath, isScrolled]);

  useEffect(() => {
    changeOpen(false);
    setCurrentPath(router.asPath);
  }, [router.asPath]);

  useEffect(() => {
    if (disableHideOnScroll) return;
    let scrollPos = 0;

    // HANDLE NAVBAR BKG COLOR TURNING WHITE
    const scrollOffsetTop = 80;
    const offsetTop = document.documentElement.offsetTop + scrollOffsetTop;

    const listener = () => {
      if (document.body.style.overflow === 'hidden' || atlas) {
        return;
      }

      // DESKTOP
      if (!isMobile()) {
        // NAVBAR STYLE
        if (window.pageYOffset > offsetTop) {
          handleScroll(true);
        } else {
          handleScroll(false);
        }
      }
      // MOBILE
      if (isMobile() && !isOpen) {
        // SWIPE DOWN
        let scroll = document.body.getBoundingClientRect().top;
        if (scroll >= 0) scroll = -1;

        if (scroll > scrollPos) {
          // STYLE NAVBAR PROPERLY
          if (window.pageYOffset > offsetTop) {
            handleScroll(true);
          } else {
            handleScroll(false);
          }

          setIsVisible(true);

          // SWIPE UP
        } else if (window.pageYOffset > 0) {
          setIsVisible(false);
        }
        scrollPos = scroll;
      }
    };

    window.addEventListener('scroll', listener);

    return () => {
      window.removeEventListener('scroll', listener);
    };
  }, [atlas, isOpen, disableHideOnScroll]);

  if (minimalMode) {
    return <MinimalNav />;
  }

  return (
    <>
      <nav
        className={clsx(
          classes.nav,
          isScrolled && classes.scrolled,
          !isVisible && classes.swipedDown,
          backgroundTransparent && !isOpen && classes.transparent,
          isOpen && classes.open,
          reminderVisible && classes.withReminder,
          noShadow && classes.noShadow,
        )}
      >
        <ScrollElement name="top" />
        <Container className={classes.inner}>
          {/* LOGO */}
          <NavLogo atlas={atlas} />
          {/* LINKS */}
          <div className={classes.linksContainer}>{links}</div>
          {/* MOBILE SUGGEST SEARCH */}
          {component}
          {/* LOGOUT */}
          {auth?.account && <NavLogout desktop auth={auth} />}
          {/* TOGGLE */}
          <div
            className={clsx(classes.toggle)}
            onClick={() => changeOpen(!isOpen)}
          >
            <NavToggle isOpen={isOpen} />
          </div>
        </Container>
      </nav>

      {/* SIDEBAR (MOBILE) */}
      <nav
        className={clsx(
          classes.sidebar,
          isOpen && classes.open,
          reminderVisible && classes.sidebarReminder,
        )}
      >
        <div className={classes.sidebarLinks}>{links}</div>

        {auth?.account && <NavLogout mobile auth={auth} />}
      </nav>
      {/* SIDEBAR OVERLAY */}
      <div
        className={clsx(classes.sidebarOverlay, isOpen && classes.open)}
        onClick={() => changeOpen(false)}
      />
    </>
  );
};
